import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { getPlatforms } from '../../services/platforms';
import PlatformCard from './PlatformCard';
import Loading from './Loading';
import { useTranslation } from 'react-i18next';

function PlatformsList({ user, setUser, handleMenuSelection }) {

    const { t } = useTranslation();

    const [platforms, setPlatforms] = useState([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null); 

    const loadPlatforms = async () => {
        setError(null);
        setIsLoading(true);
        try {
            const response = await getPlatforms();
            setPlatforms(response.platforms);
        response.platforms.sort((a, b) => a.name.localeCompare(b.name));
        } catch (error) {
            setError(t('platformsList.fetchError'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadPlatforms();
    }, []);

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    return (
        <Box
            sx={{
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                boxSizing: 'border-box',
                backgroundColor: '#F8F9FA'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'column', md: 'row' },
                    width: '100%',
                    maxWidth: '1200px',
                    height: '100vh',
                }}
            >
                {/* Platforms List - Small Screens */}
                <Box sx={{
                    display: { xs: 'flex', sm: 'flex', md: 'none'},
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: 2
                }}>
                <FormControl sx={{
                    width: { xs: '94%', sm: '84%', md: '80%' },
                }}>
                    <InputLabel id="select-label">{t('platformsList.platformLabel')}</InputLabel>
                <Select
                    labelId="select-label"
                    id="select"
                    value={selectedTab}
                    label="Plataforma"
                    onChange={(event) => handleTabChange(null, event.target.value)}
                >
                    {platforms.map((platform, index) => (
                        <MenuItem key={platform._id} value={index}>{platform.name}</MenuItem>
                    ))}                    
                </Select>
                </FormControl>
                </Box>
                {/* Platforms List (Vertical) - Large Screens */}
                <Paper
                    elevation={0}
                    sx={{
                        display: { xs: 'none', sm: 'none', md: 'flex' },
                        flexDirection: { md: 'column' },
                        width: "250px",
                        overflowY: 'auto',
                        padding: '8px',
                        border: '0.5px solid #ddd', // Optional: adds a border for better visuals
                    }}
                >
                    {platforms.map((platform, index) => (
                        <Box
                            key={index}
                            sx={{
                                padding: '8px 16px',
                                marginBottom: 1,
                                backgroundColor: selectedTab === index ? '#4680ff' : 'transparent',
                                color: selectedTab === index ? '#fff' : 'inherit',
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: selectedTab === index ? '#3578e0' : '#f5f5f5',
                                },
                            }}
                            onClick={() => handleTabChange(null, index)}
                        >
                            {platform.name}
                        </Box>
                    ))}
                </Paper>

                {/* Content Section */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'top',
                        padding: 2,
                    }}
                >
                    { platforms.length > 0 ? (
                        <PlatformCard platform={platforms[selectedTab]} user={user} setUser={setUser} handleMenuSelection={handleMenuSelection} />
                    ) : (
                        error ? (
                        <Typography variant="body1" color="textSecondary">
                            {error}
                        </Typography>
                        ) : !isLoading && (
                            <Typography variant="body1" color="textSecondary">
                                {t('platformsList.noPlatforms')}
                            </Typography>
                        )
                    )}
                </Box>
            </Box>
            <Loading text={t('platformsList.loading')} isLoading={isLoading} />
        </Box>
    );
}

export default PlatformsList;